import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RegistrationNav from './RegistrationNav';
import {
  apiCheckAccount,
  apiRequestApplicantActivation,
} from '../../../api/apiCheckAccount';

// Basic email shape check — good enough for the mockup gate.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Trade portal base URL by environment. The trade site is login-required, so
// sending an existing account there drops them straight onto the login screen.
// Local dev and staging both point at the staging trade portal; only true prod
// hosts go to the production trade portal.
const getTradeLoginUrl = (): string => {
  const host = window.location.hostname;
  const isLocal =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('192.168.');
  return isLocal || host.includes('hertex-staging.net')
    ? 'https://trade.hertex-staging.net'
    : 'https://trade.hertex.co.za';
};

type View = 'form' | 'sent';

function TradeAccountRequest() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState<View>('form');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const { exists } = await apiCheckAccount(trimmed);

      if (exists) {
        // Account already exists → send them to the trade portal to log in.
        window.location.href = getTradeLoginUrl();
        return;
      }

      // No account yet → create a pending applicant + email the activation link.
      await apiRequestApplicantActivation(trimmed);
      setView('sent');
    } catch (err: any) {
      // 409 = the account already exists (created between the check and now) →
      // treat it like the "exists" path and send them to login.
      if (err?.response?.status === 409) {
        window.location.href = getTradeLoginUrl();
        return;
      }
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <RegistrationNav />
      <Box
        sx={{
          minHeight: { xs: 'calc(100vh - 64px)', lg: 'calc(95vh - 64px)' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 6,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            border: '1px solid #E4E7EC',
          }}
        >
          {view === 'form' ? (
            <Stack spacing={3} component="form" onSubmit={handleSubmit}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Register a trade account
                </Typography>
                <Typography variant="body2" sx={{ color: '#667085' }}>
                  Enter your email to get started. If you already have an
                  account we'll take you to login, otherwise we'll send you a
                  link to activate your applicant account.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, mb: 0.75 }}
                >
                  Email address
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(error)}
                  helperText={error || ' '}
                  disabled={isSubmitting}
                  autoFocus
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#1a1a1a' },
                  textTransform: 'none',
                  py: 1.1,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} sx={{ color: '#ffffff' }} />
                ) : (
                  'Continue'
                )}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2.5} alignItems="center" textAlign="center">
              <MarkEmailReadOutlinedIcon
                sx={{ fontSize: 56, color: '#000000' }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Check your email
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                We've sent a link to <strong>{email.trim()}</strong> to activate
                your applicant account. Follow it to set your password and
                complete your trade application.
              </Typography>
              <Typography variant="caption" sx={{ color: '#98A2B3' }}>
                Didn't get it? Check your spam folder, or start again.
              </Typography>
              <Button
                variant="text"
                onClick={() => {
                  setView('form');
                  setEmail('');
                }}
                sx={{ textTransform: 'none', color: '#000000' }}
              >
                Use a different email
              </Button>
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
}

export default TradeAccountRequest;
